<script setup>
import { ref, watch, onBeforeUnmount, computed, nextTick } from 'vue'
import { GoogleMap, Polyline } from 'vue3-google-map'
import { CONSTANTS } from '@/project/configs/configs'
import { MapUtils } from './uitls/mapUtils'
import CustomMarker from './components/CustomMarker.vue'
import OverlayMarkerContainer from './components/OverlayMarkerContainer.vue'
import LocationPermissionDialog from './components/LocationPermissionDialog.vue'
import LocationPermissionBlocked from './components/LocationPermissionBlocked.vue'
import {
  traceRouteFromUser as traceRouteFromUserUtil,
  traceRouteFromUserWithPoints as traceRouteFromUserWithPointsUtil,
  traceRouteWithPoints as traceRouteWithPointsUtil,
} from './services/traceRouteUtils'
import { cleanRoute } from './services/routeHelpers'
import { getStrokeWeightForZoom } from './services/mapHelpers'
import {
  centerOnUser,
  refreshUserPosition,
  startRealTimeUserTracking,
  stopRealTimeUserTracking,
} from './services/markerHelpers'
import {
  checkGeolocationPermission,
  handlePermissionError,
  PERMISSION_STATES,
} from './utils/permissionUtils'

const mapContainer = ref(null)
const mapRef = ref(null)
const overlayContainerRef = ref(null)
const center = ref({ lat: 6.3703, lng: 2.3912 })
const markers = ref([])
const routes = ref({})
const useOverlayView = ref(true) // Use both systems simultaneously

// États de permission GPS
const permissionState = ref(PERMISSION_STATES.UNKNOWN)
const showPermissionDialog = ref(false)
const showPermissionBlocked = ref(false)
const isLoadingLocation = ref(false)

// FORCER L'AFFICHAGE POUR TEST - À SUPPRIMER APRÈS
// setTimeout(() => {
//   showPermissionBlocked.value = true
//   console.log('🚫 TEST: Écran de blocage forcé')
// }, 2000)

const emit = defineEmits(['ready', 'mapClicked'])

let currentTraceAbortController = null
let mapInstance = null

const offsetY = computed(() =>
  mapContainer.value ? mapContainer.value.offsetHeight / 6 : 150,
)

async function getCurrentUserPosition() {
  const pos = await MapUtils.getCurrentPositionWithFallback()
  return pos
}

// Exposer des méthodes pour interagir avec les marqueurs personnalisés
defineExpose({
  getCurrentUserPosition,

  // centre la carte sur l'utilisateur
  centerOnUser: () =>
    centerOnUser(mapRef.value?.map, markers.value, offsetY.value),

  traceRouteFromUser: async (id, start, end) => {
    // Créer un tableau de steps pour traceRouteFromUser
    const steps = [{ start, end }]

    // Annuler le tracé précédent si existant
    currentTraceAbortController?.abort()

    currentTraceAbortController = await traceRouteFromUserUtil({
      map: mapRef.value?.map,
      markersArray: markers.value,
      routesObj: routes.value,
      options: { offsetY: offsetY.value },
      id,
      steps,
    })
  },

  traceRouteWithPoints: async (id, start, end, ...checkpoints) => {
    // Annuler le tracé précédent si existant
    currentTraceAbortController?.abort()

    // S'assurer que les checkpoints sont dans un tableau et filtrer les valeurs invalides
    const checkpointsArray = (
      Array.isArray(checkpoints[0]) ? checkpoints[0] : checkpoints
    ).filter(
      cp =>
        cp &&
        typeof cp === 'object' &&
        cp.lat !== undefined &&
        cp.lng !== undefined,
    )

    try {
      // Utiliser la fonction existante et éprouvée de traceRouteUtils
      currentTraceAbortController = await traceRouteWithPointsUtil({
        id,
        start,
        end,
        checkpoints: checkpointsArray,
        map: mapRef.value?.map,
        markersArray: markers.value,
        routesObj: routes.value,
        options: { offsetY: offsetY.value },
      })
    } catch (error) {
      console.error('❌ Error with traceRouteUtils:', error)

      // Fallback: Try simple trace without checkpoints
      try {
        currentTraceAbortController = await traceRouteFromUserUtil({
          map: mapRef.value?.map,
          markersArray: markers.value,
          routesObj: routes.value,
          options: { offsetY: offsetY.value },
          id,
          steps: [{ start, end }],
        })
      } catch (fallbackError) {
        console.error('❌ Both methods failed:', fallbackError)
      }
    }
  },

  traceRouteFromUserWithPoints: async (id, start, end, ...checkpoints) => {
    console.log(
      '🚀 ~ id, start, end, ...checkpoints:',
      id,
      start,
      end,
      ...checkpoints,
    )
    // Annuler le tracé précédent si existant
    currentTraceAbortController?.abort()

    // S'assurer que les checkpoints sont dans un tableau et filtrer les valeurs invalides
    const checkpointsArray = (
      Array.isArray(checkpoints[0]) ? checkpoints[0] : checkpoints
    ).filter(
      cp =>
        cp &&
        typeof cp === 'object' &&
        cp.lat !== undefined &&
        cp.lng !== undefined,
    )

    currentTraceAbortController = await traceRouteFromUserWithPointsUtil({
      map: mapRef.value?.map,
      markersArray: markers.value,
      routesObj: routes.value,
      options: { offsetY: offsetY.value },
      id,
      start,
      end,
      checkpoints: checkpointsArray,
    })
  },

  cleanRoute: id => {
    cleanRoute(routes.value, markers.value, id, currentTraceAbortController)
    currentTraceAbortController = null
  },

  // Nouvelles méthodes pour les marqueurs personnalisés
  updateMarkerPosition: (markerIndex, newPosition) => {
    if (markers.value[markerIndex]) {
      markers.value[markerIndex].position = newPosition
      nextTick(() => {
        // updateCustomMarkersPositions()
      })
    }
  },

  setMarker: (marker, routeId, markerId) => {
    console.log('🚀 ~ marker, routeId, markerId:', marker, routeId, markerId)
    const markerData = {
      position: marker.position,
      isUserPosition: false,
      routeId: routeId,
      id: markerId,
      content: marker.content || '',
      iconType: marker.iconType || '',
      options: { title: marker.content || '' },
      draggable: marker.draggable || false,
      onDragEnd: marker.onDragEnd || null,
    }

    const markerIndex = markers.value.findIndex(marker => marker.id == markerId)
    if (markerIndex >= 0) {
      markers.value[markerIndex] = markerData
    } else {
      markers.value.push(markerData)
    }
  },

  refreshCustomMarkers: () => {
    nextTick(() => {
      // syncCustomMarkers()
    })
  },

  // Accès à l'instance de carte pour manipulations avancées
  getMapInstance: () => mapInstance,

  // Mettre à jour un type de marqueur spécifique
  updateMarkerType: (markerIndex, iconType, additionalData = {}) => {
    if (markers.value[markerIndex]) {
      markers.value[markerIndex].iconType = iconType
      Object.assign(markers.value[markerIndex], additionalData)
      nextTick(() => {
        // syncCustomMarkers()
      })
    }
  },

  // OverlayView system methods
  toggleMarkerSystem: () => toggleMarkerSystem(),

  getCurrentMarkerSystem: () =>
    useOverlayView.value ? 'OverlayView' : 'Vue Custom',

  // Force redraw all markers (for both systems)
  redrawAllMarkers: () => {
    if (overlayContainerRef.value) {
      overlayContainerRef.value.redrawMarkers()
    }
    // Force Vue reactivity for old system
    nextTick(() => {
      const temp = markers.value
      markers.value = []
      nextTick(() => {
        markers.value = temp
      })
    })
  },

  // Get overlay manager instance
  getOverlayManager: () => {
    return overlayContainerRef.value
      ? overlayContainerRef.value.getOverlayManager()
      : null
  },

  centerOnPosition({ lat, lng }) {
    if (mapRef.value?.map) {
      mapRef.value.map.panTo({ lat, lng })
      mapRef.value.map.setZoom(15)
    }
  },
})

const styles = [
  {
    featureType: 'all',
    elementType: 'geometry',
    stylers: [{ color: '#f5f5f5' }],
  },
  // {
  //   "featureType": "all",
  //   "elementType": "labels.icon",
  //   "stylers": [
  //     { "visibility": "off" }
  //   ]
  // },
  {
    featureType: 'all',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#616161' }],
  },
  {
    featureType: 'all',
    elementType: 'labels.text.stroke',
    stylers: [{ color: '#f5f5f5' }],
  },
  {
    featureType: 'road',
    elementType: 'geometry',
    stylers: [{ color: '#ffffff' }, { lightness: 50 }],
  },
  {
    featureType: 'road',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#e0e0e0' }],
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry',
    stylers: [{ color: '#ffffff' }],
  },
  {
    featureType: 'road.arterial',
    elementType: 'geometry',
    stylers: [{ color: '#ffffff' }],
  },
  {
    featureType: 'road.local',
    elementType: 'geometry',
    stylers: [{ color: '#ffffff' }],
  },
  {
    featureType: 'water',
    elementType: 'geometry',
    stylers: [{ color: '#90DAEE' }],
  },
  {
    featureType: 'poi',
    elementType: 'geometry',
    stylers: [{ color: '#eeeeee' }],
  },
  {
    featureType: 'transit',
    elementType: 'geometry',
    stylers: [{ color: '#e0e0e0' }],
  },
]

function onMapClick(e) {
  console.log('Click map:', { lat: e.latLng.lat(), lng: e.latLng.lng() })
  emit('mapClicked', { lat: e.latLng.lat(), lng: e.latLng.lng() })
}

function handleMarkerClick(markerData, index) {
  console.log('🔍 Vue Custom marker clicked:', index, markerData)

  const overlayTooltipId = `overlay-tooltip-${index}`
  const tooltipElement = document.getElementById(overlayTooltipId)

  if (tooltipElement) {
    // Vérifie si ce tooltip est déjà actif
    const isActive = document.body.classList.contains(
      `tooltip-visible-${index}`,
    )

    // Supprime toutes les classes tooltip visibles pour en désactiver d'autres
    document.body.classList.forEach(cls => {
      if (cls.startsWith('tooltip-visible-')) {
        document.body.classList.remove(cls)
      }
    })

    // Active ou désactive celui-ci
    if (!isActive) {
      document.body.classList.add(`tooltip-visible-${index}`)
      console.log(`🔍 Overlay tooltip ${index} visible`)
    } else {
      console.log(`🔍 Overlay tooltip ${index} caché`)
    }
  }
}

function handleMarkerDragEnd(markerData, index, event) {
  console.log('🚚 Marker dragged:', index, markerData, 'to', event.position)

  // Update marker position in the markers array
  if (event.position && markers.value[index]) {
    markers.value[index].position = event.position
  }

  // Call the onDragEnd callback if it exists
  if (markerData.onDragEnd && typeof markerData.onDragEnd === 'function') {
    markerData.onDragEnd(event)
  }
}

// Handle clicks from OverlayView system
function handleOverlayMarkerClick(markerData) {
  // Keep for compatibility but we mainly use Vue custom clicks
  console.log('🔍 Overlay marker clicked directly:', markerData)
}

// Toggle between old and new marker systems
function toggleMarkerSystem() {
  useOverlayView.value = !useOverlayView.value
  console.log(
    '🔍 Switched to:',
    useOverlayView.value ? 'OverlayView' : 'Custom Vue markers',
  )
}

// Gestion des permissions GPS
async function checkAndRequestLocationPermission() {
  try {
    permissionState.value = await checkGeolocationPermission()

    switch (permissionState.value) {
      case PERMISSION_STATES.GRANTED:
        // Permission déjà accordée, continuer normalement
        return true

      case PERMISSION_STATES.DENIED:
        // Permission refusée, afficher l'écran de blocage
        showPermissionBlocked.value = true
        return false

      case PERMISSION_STATES.PROMPT:
      case PERMISSION_STATES.UNKNOWN:
        // Demander la permission via notre dialogue
        showPermissionDialog.value = true
        return false

      default:
        return false
    }
  } catch (error) {
    console.error('Erreur lors de la vérification des permissions:', error)
    showPermissionDialog.value = true
    return false
  }
}

// Fonction pour initialiser la localisation avec permission
// initialPos: position optionnelle fournie directement (évite un double fetch GPS)
async function initializeLocationWithPermission(skipPermissionCheck = false, initialPos = null) {
  isLoadingLocation.value = true

  try {
    if (!skipPermissionCheck) {
      // Vérifier et demander les permissions
      const hasPermission = await checkAndRequestLocationPermission()

      if (!hasPermission) {
        isLoadingLocation.value = false
        return
      }
    }

    if (initialPos?.lat && initialPos?.lng) {
      // Position fournie directement → centrer immédiatement (0 ms d'attente GPS)
      await centerOnUser(mapInstance, markers.value, offsetY.value, initialPos)
    } else {
      // Pas de position fournie → essayer le cache d'abord, puis GPS
      await refreshUserPosition(markers.value, { preferCache: true })
      if (mapInstance) {
        await centerOnUser(mapInstance, markers.value, offsetY.value)
      }
    }

    // Démarrer le suivi en temps réel (watchUserPosition gère les mises à jour continues)
    startRealTimeUserTracking(markers.value, userMarker => {
      console.log('User heading updated:', userMarker.heading, 'degrees')
    })

    emit('ready')
  } catch (error) {
    console.error("Erreur lors de l'initialisation de la localisation:", error)

    // Utiliser le gestionnaire d'erreurs de permission pour obtenir des instructions spécifiques
    const errorInfo = handlePermissionError(error)

    // En cas d'erreur, vérifier si c'est une permission refusée
    if (error.message.includes('Permission')) {
      showPermissionBlocked.value = true
    }
  } finally {
    isLoadingLocation.value = false
  }
}

// Gestionnaires d'événements pour les dialogues de permission
function handlePermissionAllowed(position) {
  showPermissionDialog.value = false
  permissionState.value = PERMISSION_STATES.GRANTED

  // Passer la position obtenue lors de la demande de permission → pan immédiat
  if (mapInstance) {
    initializeLocationWithPermission(true, position)
  }
}

function handlePermissionDenied() {
  showPermissionDialog.value = false
  showPermissionBlocked.value = true
  permissionState.value = PERMISSION_STATES.DENIED
}

function handlePermissionDialogError(error) {
  showPermissionDialog.value = false
  console.error('Erreur de permission:', error)

  if (error.message.includes('Permission')) {
    showPermissionBlocked.value = true
    permissionState.value = PERMISSION_STATES.DENIED
  }
}

function handlePermissionRetry() {
  showPermissionBlocked.value = false
  checkAndRequestLocationPermission()
}

function handleUseAlternativeMethod() {
  showPermissionBlocked.value = false
  // Émettre un événement pour indiquer qu'on utilise une méthode alternative
  emit('ready')
}

function showSupport() {
  // Logique pour afficher le support
  console.log('Afficher le support')
}

// --- Initialisation ---
watch(
  () => mapRef.value?.ready,
  ready => {
    if (!ready) return
    const map = mapRef.value.map
    mapInstance = map
    MapUtils.initFromVue(map)
    map.setOptions({
      styles,
      disableDefaultUI: true,
      zoomControl: false,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      rotateControl: false,
      keyboardShortcuts: false,
      clickableIcons: false,
      // Permettre les gestes à un doigt sur mobile pour une meilleure expérience
      gestureHandling: 'greedy',
    })

    map.addListener('zoom_changed', () => {
      Object.values(routes.value).forEach(r => {
        // Épaisseur simple et remarquable
        const baseWeight = getStrokeWeightForZoom(map.getZoom())

        if (r.legIndex === 0) {
          r.strokeWeight = baseWeight + 4 // Premier segment : +4px
        } else if (r.legIndex === 1) {
          r.strokeWeight = baseWeight + 2 // Deuxième segment : +2px
        } else {
          r.strokeWeight = baseWeight // Autres segments : normal
        }
      })
    })

    // Initialiser la localisation avec gestion des permissions
    // (le suivi temps réel via watchUserPosition couvre les mises à jour continues)
    initializeLocationWithPermission()
  },
)

onBeforeUnmount(() => {
  stopRealTimeUserTracking() // Arrêter le suivi temps réel
  currentTraceAbortController?.abort()
})

// // --- Exposition publique ---
// defineExpose({
//   // centre la carte sur l'utilisateur
//   centerOnUser: () =>
//     centerOnUser(mapRef.value?.map, markers.value, offsetY.value),

//   // trace une route entre plusieurs steps (non implémenté)
//   traceRouteForSteps: async (id, steps) => {
//     // Non implémenté
//   },

//   traceRouteFromUser: async (id, start, end) => {
//     const steps = [{ start, end }]
//     currentTraceAbortController = await traceRouteFromUser({
//       map: mapRef.value?.map,
//       markersArray: markers.value,
//       routesObj: routes.value,
//       options: { offsetY: offsetY.value },
//       id,
//       steps,
//     })
//   },

//   traceRouteWithPoints: async (id, start, end, ...checkpoints) => {
//     const checkpointsArray = Array.isArray(checkpoints[0]) ? checkpoints[0] : checkpoints

//     try {
//       currentTraceAbortController = await traceRouteWithPoints({
//         id,
//         start,
//         end,
//         checkpoints: checkpointsArray,
//         map: mapRef.value?.map,
//         markersArray: markers.value,
//         routesObj: routes.value,
//         options: { offsetY: offsetY.value }
//       })
//     } catch (error) {
//       console.error('❌ Error with traceRouteUtils:', error)
//       try {
//         currentTraceAbortController = await traceRouteFromUser({
//           map: mapRef.value?.map,
//           markersArray: markers.value,
//           routesObj: routes.value,
//           options: { offsetY: offsetY.value },
//           id,
//           start,
//           end
//         })
//       } catch (fallbackError) {
//         console.error('❌ Both methods failed:', fallbackError)
//       }
//     }
//   },

//   traceRouteFromUserWithPoints: async (id, start, end, ...checkpoints) => {
//     const checkpointsArray = Array.isArray(checkpoints[0]) ? checkpoints[0] : checkpoints
//     currentTraceAbortController = await traceRouteFromUserWithPoints({
//       map: mapRef.value?.map,
//       markersArray: markers.value,
//       routesObj: routes.value,
//       options: { offsetY: offsetY.value },
//       id,
//       start,
//       end,
//       checkpoints: checkpointsArray,
//     })
//   },

//   cleanRoute: id =>
//     cleanRoute(routes.value, markers.value, id, currentTraceAbortController),

//   // Accès à l'instance de carte
//   getMapInstance: () => mapInstance,
// })
</script>

<template>
  <div class="relative w-full h-full">
    <div class="mapContainer w-full h-full relative">
      <div class="absolute w-full h-[calc(100%+20px)]">
        <div ref="mapContainer" class="w-full h-full relative">
          <GoogleMap
            ref="mapRef"
            :api-key="CONSTANTS.GOOGLE_MAP_API_KEY"
            nomap-id="'b693176c5686ff86e32dc2a2'"
            :center="center"
            :zoom="15"
            class="w-full h-full"
            :disableDefaultUI="true"
            :draggable="true"
            :zoomControl="false"
            :mapTypeControl="false"
            :streetViewControl="false"
            :fullscreenControl="false"
            @click="onMapClick"
          >
            <!-- tracés dynamiques -->
            <Polyline
              v-for="(route, id) in routes"
              :key="id"
              :options="route"
            />
          </GoogleMap>

          <OverlayMarkerContainer
            ref="overlayContainerRef"
            :markers="markers"
            :map-instance="mapInstance"
            @marker-click="handleOverlayMarkerClick"
          />

          <div
            class="customMarkersContainer absolute w-full h-full top-0 left-0 opacity-0 pointer-events-none z-40"
          >
            <CustomMarker
              v-for="(markerData, index) in markers"
              :key="`marker-${index}`"
              :marker-data="markerData"
              :map-instance="mapInstance"
              @click="markerData => handleMarkerClick(markerData, index)"
              @dragend="event => handleMarkerDragEnd(markerData, index, event)"
            />
          </div>
        </div>
      </div>
    </div>

    <Teleport to="body">
      <!-- Dialogue de demande de permission GPS -->
      <LocationPermissionDialog
        :is-visible="showPermissionDialog"
        @allow="handlePermissionAllowed"
        @deny="handlePermissionDenied"
        @error="handlePermissionDialogError"
      />

      <!-- Écran de blocage lorsque la permission est refusée -->
      <LocationPermissionBlocked
        v-if="showPermissionBlocked"
        :show-alternative-option="true"
        @permission-granted="handlePermissionRetry"
        @use-alternative="handleUseAlternativeMethod"
        @show-support="showSupport"
      />
    </Teleport>
  </div>
</template>

<style>
/* Par défaut tous les tooltips sont cachés */
.overlay-tooltip {
  /* display: none; */
  opacity: 0;
  transform: translateY(-10px);
}

.overlay-tooltip {
  /* animation: fadeIn 0.2s ease-in-out; */
}

/* Quand le body a la classe correspondante, le tooltip s'affiche */
body.tooltip-visible-0 #overlay-tooltip-0,
body.tooltip-visible-1 #overlay-tooltip-1,
body.tooltip-visible-2 #overlay-tooltip-2,
body.tooltip-visible-3 #overlay-tooltip-3,
body.tooltip-visible-4 #overlay-tooltip-4,
body.tooltip-visible-5 #overlay-tooltip-5,
body.tooltip-visible-6 #overlay-tooltip-6,
body.tooltip-visible-7 #overlay-tooltip-7,
body.tooltip-visible-8 #overlay-tooltip-8,
body.tooltip-visible-9 #overlay-tooltip-9,
body.tooltip-visible-10 #overlay-tooltip-10,
body.tooltip-visible-11 #overlay-tooltip-11,
body.tooltip-visible-12 #overlay-tooltip-12,
body.tooltip-visible-13 #overlay-tooltip-13,
body.tooltip-visible-14 #overlay-tooltip-14,
body.tooltip-visible-15 #overlay-tooltip-15,
body.tooltip-visible-16 #overlay-tooltip-16,
body.tooltip-visible-17 #overlay-tooltip-17,
body.tooltip-visible-18 #overlay-tooltip-18,
body.tooltip-visible-19 #overlay-tooltip-19 {
  display: block !important;
  transform: translateY(0);
  opacity: 1;
  /* width: 100px!important;
  height: 100px!important;
  background-color: red!important; */
  /* animation: fadeIn 0.2s ease-in-out; */
}

/* @keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
} */

/* Rotation du marker utilisateur basée sur le heading */
:root {
  --user-heading: 0deg;
}

.user-heading-rotation {
  transform: rotate(var(--user-heading, 0deg));
}
</style>
